<template>
  <div class="obs-widget" :class="{ 'is-busy': !!pendingAction }">
    <div class="obs-widget__top">
      <select :value="nodeId" class="form-control form-control-sm" :disabled="!!pendingAction" @change="onNodeChange">
        <option value="">-- Node --</option>
        <option v-for="n in nodes" :key="n.id" :value="n.id">{{ n.name }}</option>
      </select>
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary"
        title="Refresh"
        :disabled="!nodeId || !!pendingAction"
        @click="refreshAll"
      >
        <i class="fas" :class="pendingAction === 'refresh' ? 'fa-spinner fa-spin' : 'fa-sync-alt'" />
      </button>
    </div>

    <div class="obs-widget__status">
      <div class="obs-status" :class="{ 'is-on': streaming }">
        <span class="obs-status__dot" />
        <span class="obs-status__label">LIVE</span>
        <span class="obs-status__time">{{ streaming ? streamTimecode : '--:--:--' }}</span>
      </div>
      <div class="obs-status" :class="{ 'is-on': recording }">
        <span class="obs-status__dot" />
        <span class="obs-status__label">REC</span>
        <span class="obs-status__time">{{ recording ? recordTimecode : '--:--:--' }}</span>
      </div>
    </div>

    <div class="obs-widget__scenes">
      <div class="obs-widget__section-title">
        <span>Scenes</span>
        <span v-if="pendingAction === 'refresh' || pendingAction === 'scenes'" class="obs-widget__hint">
          <i class="fas fa-spinner fa-spin" /> Loading…
        </span>
        <span v-else-if="sceneName" class="obs-widget__hint" :title="sceneName">{{ sceneName }}</span>
      </div>
      <div v-if="!nodeId" class="obs-widget__empty">Select a node</div>
      <div v-else-if="!scenes.length && pendingAction !== 'refresh' && pendingAction !== 'scenes'" class="obs-widget__empty">No scenes</div>
      <div v-else class="obs-scene-grid" :class="{ 'is-locked': !!pendingAction }">
        <button
          v-for="(s, i) in scenes"
          :key="s"
          type="button"
          class="obs-scene"
          :class="{
            'is-program': s === sceneName && pendingAction !== `scene:${s}`,
            'is-pending': pendingAction === `scene:${s}`,
          }"
          :disabled="!!pendingAction"
          :title="s"
          @click="selectScene(s)"
        >
          <span class="obs-scene__idx">{{ i + 1 }}</span>
          <span class="obs-scene__name">{{ s }}</span>
          <i v-if="pendingAction === `scene:${s}`" class="fas fa-spinner fa-spin obs-scene__spin" />
        </button>
      </div>
    </div>

    <div class="obs-widget__controls">
      <button
        type="button"
        class="obs-ctrl obs-ctrl--stream"
        :class="{ 'is-active': streaming, 'is-loading': pendingAction === 'stream' }"
        :disabled="!nodeId || !!pendingAction"
        @click="toggleStream"
      >
        <i class="fas" :class="pendingAction === 'stream' ? 'fa-spinner fa-spin' : (streaming ? 'fa-broadcast-tower' : 'fa-play')" />
        <span>{{ pendingAction === 'stream' ? 'Working…' : (streaming ? 'Stop Stream' : 'Start Stream') }}</span>
      </button>
      <button
        type="button"
        class="obs-ctrl obs-ctrl--record"
        :class="{ 'is-active': recording, 'is-loading': pendingAction === 'record' }"
        :disabled="!nodeId || !!pendingAction"
        @click="toggleRecord"
      >
        <i
          class="fas"
          :class="pendingAction === 'record' ? 'fa-spinner fa-spin' : (recording ? 'fa-stop' : 'fa-circle')"
        />
        <span>{{ pendingAction === 'record' ? 'Working…' : (recording ? 'Stop Rec' : 'Start Rec') }}</span>
      </button>
    </div>

    <div v-if="error" class="obs-widget__error">{{ error }}</div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

const POLL_MS = 2000

export default {
  props: {
    config: { type: Object, default: () => ({}) },
    widgetId: { type: String, default: '' },
    tabActive: { type: Boolean, default: true },
  },
  data() {
    return {
      sceneName: '',
      scenes: [],
      streaming: false,
      recording: false,
      streamTimecode: '00:00:00',
      recordTimecode: '00:00:00',
      pendingAction: '',
      error: '',
      pollTimer: null,
    }
  },
  computed: {
    ...mapGetters('mission', ['nodes']),
    nodeId() {
      return this.config.nodeId || ''
    },
  },
  watch: {
    tabActive(active) {
      if (!this.nodeId) return
      if (active) this.startPoll()
      else this.stopPoll()
    },
    nodeId: {
      immediate: true,
      handler(id) {
        this.stopPoll()
        this.scenes = []
        this.sceneName = ''
        this.streaming = false
        this.recording = false
        this.streamTimecode = '00:00:00'
        this.recordTimecode = '00:00:00'
        this.pendingAction = ''
        this.error = ''
        if (id) {
          this.refreshAll()
          if (this.tabActive) this.startPoll()
        }
      },
    },
  },
  beforeDestroy() {
    this.stopPoll()
  },
  methods: {
    ...mapActions('mission', ['sendCommand']),
    ...mapActions('workspace', ['updateWidgetConfig']),
    onNodeChange(e) {
      if (!this.widgetId) return
      this.updateWidgetConfig({ widgetId: this.widgetId, config: { nodeId: e.target.value } })
    },
    startPoll() {
      this.stopPoll()
      this.pollTimer = setInterval(() => {
        if (this.nodeId && !document.hidden && !this.pendingAction) this.refreshStatus(true)
      }, POLL_MS)
    },
    stopPoll() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    beginAction(key) {
      if (this.pendingAction) return false
      this.pendingAction = key
      this.error = ''
      return true
    },
    endAction() {
      this.pendingAction = ''
    },
    formatTimecode(tc) {
      if (!tc || typeof tc !== 'string') return '00:00:00'
      return tc.split('.')[0] || '00:00:00'
    },
    parsePayload(res) {
      const msg = res && res.result
      if (!msg) return null
      if (msg.type === 'error' || msg.error) {
        throw new Error(msg.error || 'Command failed')
      }
      let payload = msg.payload
      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload)
        } catch (e) {
          return null
        }
      }
      return payload
    },
    async cmd(action, payload = {}) {
      if (!this.nodeId) return null
      const res = await this.sendCommand({ node_id: this.nodeId, adapter: 'obs', action, payload })
      return this.parsePayload(res)
    },
    async refreshAll() {
      if (!this.nodeId || !this.beginAction('refresh')) return
      try {
        await this.loadScenes()
        await this.refreshStatus(false)
      } catch (e) {
        this.error = e.response?.data?.message || e.message || 'Error'
      } finally {
        this.endAction()
      }
    },
    sceneLabel(raw) {
      if (raw == null) return ''
      if (typeof raw === 'string') return raw
      return String(raw.sceneName || raw.name || '')
    },
    applyProgramScene(name) {
      const n = String(name || '')
      if (n && this.scenes.includes(n)) this.sceneName = n
      else if (this.sceneName && !this.scenes.includes(this.sceneName)) this.sceneName = ''
    },
    async loadScenes() {
      if (!this.nodeId) return
      const data = (await this.cmd('GetSceneList')) || {}
      const raw = Array.isArray(data.scenes) ? data.scenes : []
      this.scenes = raw.map((s) => this.sceneLabel(s)).filter(Boolean)
      this.applyProgramScene(data.currentProgramSceneName || data.sceneName)
    },
    async refreshStatus(soft = false) {
      if (!this.nodeId) return
      if (soft && this.pendingAction) return
      try {
        const [stream, record, program] = await Promise.all([
          this.cmd('GetStreamStatus'),
          this.cmd('GetRecordStatus'),
          this.cmd('GetCurrentProgramScene').catch(() => null),
        ])
        if (this.pendingAction && soft) return
        this.streaming = !!(stream && stream.outputActive)
        this.streamTimecode = this.formatTimecode(stream && stream.outputTimecode)
        this.recording = !!(record && record.outputActive)
        this.recordTimecode = this.formatTimecode(record && record.outputTimecode)
        this.applyProgramScene(
          (program && (program.currentProgramSceneName || program.sceneName)) || '',
        )
        if (!soft) this.error = ''
      } catch (e) {
        if (!soft) this.error = e.response?.data?.message || e.message || 'Status error'
      }
    },
    async selectScene(name) {
      if (!name || name === this.sceneName) return
      if (!this.beginAction(`scene:${name}`)) return
      const prev = this.sceneName
      try {
        await this.cmd('SetCurrentProgramScene', { sceneName: name })
        this.sceneName = name
      } catch (e) {
        this.sceneName = prev
        this.error = e.response?.data?.message || e.message || 'Switch failed'
      } finally {
        this.endAction()
      }
    },
    async toggleStream() {
      if (!this.beginAction('stream')) return
      try {
        await this.cmd(this.streaming ? 'StopStream' : 'StartStream')
        await this.refreshStatus(false)
      } catch (e) {
        this.error = e.response?.data?.message || e.message || 'Stream error'
      } finally {
        this.endAction()
      }
    },
    async toggleRecord() {
      if (!this.beginAction('record')) return
      try {
        await this.cmd(this.recording ? 'StopRecord' : 'StartRecord')
        await this.refreshStatus(false)
      } catch (e) {
        this.error = e.response?.data?.message || e.message || 'Record error'
      } finally {
        this.endAction()
      }
    },
  },
}
</script>

<style scoped>
.obs-widget {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  width: 100%;
  height: 100%;
  min-height: 0;
  color: var(--ws-text);
  font-size: 0.8em;
}

.obs-widget__top {
  display: flex;
  gap: 0.35rem;
  align-items: center;
  flex-shrink: 0;
}
.obs-widget__top select {
  flex: 1;
  min-width: 0;
}

.obs-widget__status {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.35rem;
  flex-shrink: 0;
}

.obs-status {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  background: var(--ws-panel, #f4f6f9);
  border: 1px solid var(--ws-panel-border, #e3e6ef);
  border-radius: 4px;
  padding: 0.3rem 0.45rem;
  font-variant-numeric: tabular-nums;
}
.obs-status__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--ws-text-muted, #6c757d);
  flex-shrink: 0;
  opacity: 0.5;
}
.obs-status__label {
  font-weight: 700;
  letter-spacing: 0.04em;
  font-size: 0.7em;
  color: var(--ws-text-muted, #6c757d);
}
.obs-status__time {
  margin-left: auto;
  color: var(--ws-text-muted, #6c757d);
  font-size: 0.72em;
}
.obs-status.is-on {
  background: rgba(192, 57, 43, 0.12);
  border-color: var(--ws-live, #c23a3a);
}
.obs-status.is-on .obs-status__dot {
  background: var(--ws-live, #c23a3a);
  opacity: 1;
  box-shadow: 0 0 6px rgba(194, 58, 58, 0.55);
  animation: obs-pulse 1.2s ease-in-out infinite;
}
.obs-status.is-on .obs-status__label,
.obs-status.is-on .obs-status__time {
  color: var(--ws-live, #c23a3a);
  font-weight: 700;
}

.obs-widget__scenes {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.obs-widget__section-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
  font-size: 0.68em;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--ws-text-muted, #6c757d);
  flex-shrink: 0;
}

.obs-widget__hint {
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 60%;
  color: var(--ws-live, #c23a3a);
}

.obs-widget__empty {
  padding: 0.75rem;
  color: var(--ws-text-muted, #6c757d);
  text-align: center;
}

.obs-scene-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(108px, 1fr));
  gap: 0.35rem;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  align-content: start;
}

.obs-scene {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 0.12rem;
  min-height: 48px;
  padding: 0.4rem 0.5rem;
  border: 1px solid var(--ws-widget-border, #e9ecef);
  background: var(--ws-panel, #f4f6f9);
  color: var(--ws-text);
  border-radius: 6px;
  cursor: pointer;
  text-align: left;
  line-height: 1.15;
}
.obs-scene:hover:not(:disabled) {
  border-color: var(--ws-accent, #3d7eff);
  background: var(--ws-accent-soft, rgba(61, 126, 255, 0.14));
}
.obs-scene.is-program {
  border-color: var(--ws-live, #c23a3a);
  background: rgba(192, 57, 43, 0.16);
  box-shadow: inset 0 0 0 1px rgba(192, 57, 43, 0.25);
}
.obs-scene.is-pending {
  opacity: 0.7;
  cursor: wait;
}
.obs-scene:disabled:not(.is-pending) {
  opacity: 0.45;
  cursor: not-allowed;
}
.obs-scene-grid.is-locked .obs-scene:not(.is-pending) {
  pointer-events: none;
}
.obs-scene__idx {
  font-size: 0.62em;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--ws-text-muted, #6c757d);
}
.obs-scene.is-program .obs-scene__idx {
  color: var(--ws-live, #c23a3a);
}
.obs-scene__name {
  width: 100%;
  font-size: 0.82em;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.obs-scene__spin {
  color: var(--ws-accent, #3d7eff);
  font-size: 0.75em;
  align-self: flex-end;
}

.obs-widget__controls {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.35rem;
  flex-shrink: 0;
}

.obs-ctrl {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  min-height: 36px;
  border: 1px solid var(--ws-ctrl-border, #d5d9e0);
  background: var(--ws-ctrl-bg, #eceff3);
  color: var(--ws-text);
  border-radius: 6px;
  font-size: 0.72em;
  font-weight: 600;
  cursor: pointer;
  padding: 0.35rem 0.45rem;
  line-height: 1.15;
}
.obs-ctrl:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.obs-ctrl.is-loading {
  opacity: 1;
  cursor: wait;
}
.obs-ctrl:not(:disabled):hover {
  background: var(--ws-ctrl-hover, #dfe3ea);
}
.obs-ctrl.is-active {
  background: rgba(192, 57, 43, 0.16);
  border-color: var(--ws-live, #c23a3a);
  color: var(--ws-live, #c23a3a);
}
.obs-ctrl--record .fa-circle {
  color: var(--ws-live, #c23a3a);
  font-size: 0.7em;
}

.obs-widget__error {
  color: var(--ws-error, #c0392b);
  font-size: 0.7em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-shrink: 0;
}

@keyframes obs-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
}
</style>
